import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { navGroups, topLevelLinks } from '../data/navigation';
import { Wordmark } from './Wordmark';
import { TopStatement } from './TopStatement';
export function Header() {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>('Products');
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    setOpenGroup(null);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenGroup(null);
        setMobileOpen(false);
      }
    }
    function onPointerDown(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);
  return <header className="sticky top-0 z-50 w-full" data-id="element-41" data-mp="2ho3pcrcqcjju">
      <TopStatement data-id="element-42" data-mp="2hpoh697ntoe0" />
      <div className="border-b border-rule bg-warm/95 backdrop-blur-sm" data-id="element-43" data-mp="3fbcd6t07e4p0">
        <div className="mx-auto flex max-w-shell items-stretch justify-between px-5 lg:px-10" data-id="element-44" data-mp="3fakkpr1wkn2y">
          <div className="flex min-w-0 items-center py-3" data-id="element-45" data-mp="3ad12kg233q5q">
            <Wordmark data-id="element-46" data-mp="35kr5mg2jxpty" />
          </div>

          {/* Desktop navigation */}
          <div ref={navRef} className="hidden items-stretch gap-1 lg:flex" onBlur={e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOpenGroup(null);
          }
        }} data-id="element-47" data-mp="30mpmjv54lqih">
            <nav aria-label="Primary" className="flex items-stretch" data-id="element-48" data-mp="3t9ad4tds1ykz">
              {navGroups.map(group => {
              const isOpen = openGroup === group.label;
              const panelId = `nav-panel-${group.label.toLowerCase().replace(/\s+/g, '-')}`;
              return <div key={group.label} className="relative flex items-stretch" onMouseEnter={() => setOpenGroup(group.label)} onMouseLeave={() => setOpenGroup(null)} data-id="element-49" data-mp="1mmbc5axym4kz">
                    <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenGroup(isOpen ? null : group.label)} className="flex min-h-[44px] items-center gap-1.5 px-4 text-[14px] font-medium text-navy transition-colors duration-150 ease-precise hover:text-copper" data-id="element-50" data-mp="1cmc7hp4nuyde">
                      {group.label}
                      <ChevronDownIcon aria-hidden="true" className={`h-3.5 w-3.5 text-aluminum transition-transform duration-150 ease-precise ${isOpen ? 'rotate-180' : ''}`} data-id="element-51" data-mp="mf68skr9qs2u" />
                    </button>
                    <div id={panelId} hidden={!isOpen} className="absolute left-0 top-full w-[264px] border border-rule border-t-copper bg-white shadow-[0_18px_40px_-28px_rgba(11,20,32,0.45)]" data-id="element-52" data-mp="rbwq1bet7tpu">
                      <ul className="py-2" data-id="element-53" data-mp="1f2k9mty387s8">
                        {group.links.map(link => <li key={link.href} data-id="element-54" data-mp="1a50jows126kg">
                            <Link to={link.href} data-analytics="product-category-click" data-analytics-label={link.label} className="flex min-h-[44px] items-center px-4 text-[14px] text-graphite transition-colors duration-150 ease-precise hover:bg-warm hover:text-copper" data-id="element-55" data-mp="3gu6v6fz7nahb">
                              {link.label}
                            </Link>
                          </li>)}
                      </ul>
                    </div>
                  </div>;
            })}
              {topLevelLinks.map(link => <Link key={link.href} to={link.href} className="flex min-h-[44px] items-center px-4 text-[14px] font-medium text-navy transition-colors duration-150 ease-precise hover:text-copper" data-id="element-56" data-mp="1p6haaedaxelp">
                  {link.label}
                </Link>)}
            </nav>
            <div className="flex items-center pl-4" data-id="element-57" data-mp="11fva199jgym9">
              <Link to="/#product-guidance" data-analytics="product-guidance-cta" data-analytics-location="header" className="inline-flex min-h-[44px] items-center bg-cta px-5 text-[13px] font-semibold tracking-[0.02em] text-navy transition-colors duration-150 ease-precise hover:bg-copper hover:text-white" data-id="element-58" data-mp="15i86t86b5m6w">
                Request Product Guidance
              </Link>
            </div>
          </div>

          {/* Mobile trigger */}
          <button type="button" aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen(v => !v)} className="-mr-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-navy lg:hidden" data-id="element-59" data-mp="f2h5oxiydnll">
            {mobileOpen ? <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-60" data-mp="3sfsy9bmlluev" /> : <MenuIcon aria-hidden="true" className="h-6 w-6" data-id="element-61" data-mp="3shvljnwd99fw" />}
            <span className="sr-only" data-id="element-62" data-mp="3nh7yfdb9dcqy">
              {mobileOpen ? 'Close menu' : 'Open menu'}
            </span>
          </button>
        </div>

        {/* Mobile menu */}
        <div id="mobile-menu" hidden={!mobileOpen} className="max-h-[calc(100vh-9rem)] overflow-y-auto border-t border-rule bg-white lg:hidden" data-id="element-63" data-mp="3isgl7tfb2maj">
          <nav aria-label="Mobile primary" className="px-5 pb-6 pt-2" data-id="element-64" data-mp="38tham5azcgkw">
            <ul className="divide-y divide-rule" data-id="element-65" data-mp="38vu156uszz9k">
              {navGroups.map(group => {
              const isOpen = mobileGroup === group.label;
              const panelId = `m-panel-${group.label.toLowerCase().replace(/\s+/g, '-')}`;
              return <li key={group.label} data-id="element-66" data-mp="3rsv7av03g5f3">
                    <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setMobileGroup(isOpen ? null : group.label)} className="flex min-h-[52px] w-full items-center justify-between text-left text-[15px] font-medium text-navy" data-id="element-67" data-mp="3rrk7f69rrht9">
                      {group.label}
                      <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 text-aluminum transition-transform duration-150 ease-precise ${isOpen ? 'rotate-180' : ''}`} data-id="element-68" data-mp="23szwo8wg2xt0" />
                    </button>
                    <ul id={panelId} hidden={!isOpen} className="pb-3" data-id="element-69" data-mp="1kw86dfihjifg">
                      {group.links.map(link => <li key={link.href} data-id="element-70" data-mp="1yw9i5q0aga9p">
                          <Link to={link.href} data-analytics="product-category-click" data-analytics-label={link.label} className="flex min-h-[44px] items-center border-l border-rule pl-4 text-[14px] text-graphite" data-id="element-71" data-mp="1yxc4dkmo1oja">
                            {link.label}
                          </Link>
                        </li>)}
                    </ul>
                  </li>;
            })}
              {topLevelLinks.map(link => <li key={link.href} data-id="element-72" data-mp="1dj0k0pughaak">
                  <Link to={link.href} className="flex min-h-[52px] items-center text-[15px] font-medium text-navy" data-id="element-73" data-mp="1icbud0yh8978">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
            <Link to="/#product-guidance" data-analytics="product-guidance-cta" data-analytics-location="mobile-menu" className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center bg-cta px-5 text-[14px] font-semibold text-navy" data-id="element-74" data-mp="1wbup8s2a1o1l">
              Request Product Guidance
            </Link>
          </nav>
        </div>
      </div>
    </header>;
}